import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';

/* ───────────────────────────────────────────────────────────────────────────
   Contrato — CRUD /fiscal/freight-rules (FiscalConfigController)

   Espelha `FreightRuleResponseDto` e os DTOs de create/update/query
   (presentation/http/dto/fiscal-config.dto.ts).

   Tipos de frete: CIF | FOB
   Base de cobrança: VALOR | PESO | QUANTIDADE

   Envelope paginado {data, total, page, limit, totalPages} nas listagens;
   filtros ufOrigem/ufDestino/ncmPrefix/enabled. O tenant vem da sessão.
   ─────────────────────────────────────────────────────────────────────────── */

export interface FreightRule {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  ufOrigem: string;
  ufDestino: string;
  ncmPrefix: string | null;
  tipoFrete: string;
  baseCobranca: string;
  aliquotaIcms: number | null;
  aliquotaDifal: number | null;
  valorMinimo: number | null;
  valorPorKg: number | null;
  valorPorUnidade: number | null;
  percentualValor: number | null;
  valorFixo: number | null;
  enabled: boolean;
  effectiveSince: string;
  effectiveUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FreightRuleInput {
  name: string;
  description?: string;
  ufOrigem: string;
  ufDestino: string;
  ncmPrefix?: string;
  tipoFrete: string;
  baseCobranca: string;
  aliquotaIcms?: number;
  aliquotaDifal?: number;
  valorMinimo?: number;
  valorPorKg?: number;
  valorPorUnidade?: number;
  percentualValor?: number;
  valorFixo?: number;
  enabled?: boolean;
  effectiveSince: string;
  effectiveUntil?: string;
}

export interface PaginatedFreightRules {
  data: FreightRule[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface FreightRuleQuery {
  ufOrigem?: string;
  ufDestino?: string;
  ncmPrefix?: string;
  enabled?: boolean;
  page?: number;
  limit?: number;
}

const KEY = ['fiscal', 'freight-rules'] as const;

export function useFreightRules(params?: FreightRuleQuery) {
  return useQuery({
    queryKey: [KEY, params] as const,
    queryFn: () =>
      apiClient.get<PaginatedFreightRules>('/fiscal/freight-rules', {
        params: {
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 50),
          ...(params?.ufOrigem ? { ufOrigem: params.ufOrigem } : {}),
          ...(params?.ufDestino ? { ufDestino: params.ufDestino } : {}),
          ...(params?.ncmPrefix ? { ncmPrefix: params.ncmPrefix } : {}),
          ...(params?.enabled !== undefined ? { enabled: String(params.enabled) } : {}),
        },
      }),
  });
}

export function useCreateFreightRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FreightRuleInput) =>
      apiClient.post<FreightRule>('/fiscal/freight-rules', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateFreightRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: FreightRuleInput & { id: string }) =>
      apiClient.put<FreightRule>(`/fiscal/freight-rules/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteFreightRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/fiscal/freight-rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}